import { z } from 'zod';

/**
 * The effective experience a process is rendered from (ADR-0030).
 *
 * The server resolves the inheritance chain — pack, tenant, customer, process, role — evaluates every
 * `visibleWhen` and `requiredWhen` against the record's facts and the viewer's scope, and answers with what is
 * left. Nothing here is re-derived: no inheritance, no ZEL, no knowledge of which scope a rule came from. The
 * renderer renders this, and the command path validates against the same contract, so the form and the server
 * cannot disagree about what is required unless the server changed between the two calls.
 *
 * It lives beside the screen, form and dashboard definitions because that is what it is: a definition, already
 * resolved. `EffectiveExperience` mirrors the Java record of the same name.
 */

/**
 * `ProcessExperienceDefinition.ValidationRule`.
 *
 * `min`/`max`/`minLength`/`maxLength`/`pattern` are checkable here and are checked, so an operator is told
 * before the round trip. `expression` is ZEL and is evaluated only by the server — a rule carrying one is
 * carried through to the submit check rather than guessed at.
 */
export const experienceValidationSchema = z.object({
  code: z.string(),
  min: z.number().nullish(),
  max: z.number().nullish(),
  minLength: z.number().int().nullish(),
  maxLength: z.number().int().nullish(),
  pattern: z.string().nullish(),
  expression: z.string().nullish(),
  message: z.string().nullish(),
});
export type ExperienceValidation = z.infer<typeof experienceValidationSchema>;

/** `EffectiveExperience.EffectiveComponent`. `required` is already evaluated; the command enforces exactly it. */
export const effectiveComponentSchema = z.object({
  code: z.string(),
  componentType: z.string(),
  label: z.string().nullish(),
  binding: z.string().nullish(),
  visible: z.boolean(),
  required: z.boolean(),
  readOnly: z.boolean(),
  defaultValue: z.unknown().nullish(),
  validations: z.array(experienceValidationSchema).default([]),
  props: z.record(z.string(), z.unknown()).default({}),
});
export type EffectiveComponent = z.infer<typeof effectiveComponentSchema>;

/** `EffectiveExperience.EffectiveSection`. `repeatFor` names a collection; bindings are then per element. */
export const effectiveSectionSchema = z.object({
  code: z.string(),
  title: z.string().nullish(),
  /** L-05: what the section is for, under its title. */
  description: z.string().nullish(),
  columns: z.number().int(),
  visible: z.boolean(),
  repeatFor: z.string().nullish(),
  components: z.array(effectiveComponentSchema),
});
export type EffectiveSection = z.infer<typeof effectiveSectionSchema>;

/**
 * `StepHelpDefinition.Block` — one piece of the explanation behind a step's help line.
 *
 * A block vocabulary rather than markup, deliberately: a definition is tenant-authored and reaches a
 * browser, so it must not be able to carry HTML. Paragraphs and lists are what the approved help pages are
 * made of, and they are all this needs to express.
 */
export const stepHelpBlockSchema = z.object({
  type: z.string(),
  text: z.string().nullish(),
  items: z.array(z.string()).default([]),
});
export type StepHelpBlock = z.infer<typeof stepHelpBlockSchema>;

/** `StepHelpDefinition.Help` — the explanation the help line opens. */
export const stepHelpSchema = z.object({
  hint: z.string().nullish(),
  actionLabel: z.string().nullish(),
  title: z.string().nullish(),
  body: z.array(stepHelpBlockSchema).default([]),
  links: z.array(z.object({ label: z.string(), href: z.string() })).default([]),
});
export type StepHelp = z.infer<typeof stepHelpSchema>;

/** `EffectiveExperience.EffectiveStep`. */
export const effectiveStepSchema = z.object({
  code: z.string(),
  title: z.string(),
  /** The sentence under the heading. The approved guided flow asks a question as the title and explains it here. */
  description: z.string().nullish(),
  /** Three or four words beside the step's number in the rail — "Mode and service", not the description again. */
  railHint: z.string().nullish(),
  /** The one restrained line of help offered for this question; the prompt, and on its own the whole help. */
  helpText: z.string().nullish(),
  /** The explanation behind that line, when the definition carries one. */
  help: stepHelpSchema.nullish(),
  order: z.number().int(),
  visible: z.boolean(),
  skippable: z.boolean(),
  sections: z.array(effectiveSectionSchema),
});
export type EffectiveStep = z.infer<typeof effectiveStepSchema>;

/** `EffectiveExperience`. `version` is recorded on the transaction so an in-flight one is judged by it (ADR-0033). */
export const effectiveExperienceSchema = z.object({
  businessProcess: z.string(),
  key: z.string(),
  version: z.number().int(),
  steps: z.array(effectiveStepSchema),
});
export type EffectiveExperience = z.infer<typeof effectiveExperienceSchema>;

/** `ExperienceComponentContribution.ComponentType`: what the platform and its modules publish. */
export const experienceComponentTypeSchema = z.object({
  code: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  valueType: z.string(),
  supportsRequired: z.boolean(),
});
export type ExperienceComponentType = z.infer<typeof experienceComponentTypeSchema>;

/** Steps a viewer actually sees, in the order the server put them. */
export function visibleSteps(experience: EffectiveExperience): EffectiveStep[] {
  return [...experience.steps].filter((s) => s.visible).sort((a, b) => a.order - b.order);
}

export function visibleSections(step: EffectiveStep): EffectiveSection[] {
  return step.sections.filter((s) => s.visible);
}

export function visibleComponents(section: EffectiveSection): EffectiveComponent[] {
  return section.components.filter((c) => c.visible);
}
